import { css, cx } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useMemoizedFn } from 'ahooks';
import { Popover, PopoverProps } from 'antd';
import { isUndefined } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import StickerGrids, { layoutParser, sizeParser, StickerGridsProps, Sticker } from './StickerGrids';
import StickerTabOrig, { StickerTabProps } from './StickerTab';
import { useSetConversationToggle } from 'hooks/business/conversationsHooks/useConversationHotKey';

export interface StickerPopoverDatas<R> extends Sticker<R> {
  /**
   * 渲染tab时优先使用tabLabel
   */
  packLabel?: React.ReactNode;
  packTitle?: React.ReactNode;
}
export interface StickerPopoverProps<R> extends Omit<PopoverProps, 'content'> {
  stickers: StickerPopoverDatas<R>[];
  layout?: StickerGridsProps<R>['layout'];
  size?: StickerGridsProps<R>['size'];
  onChange?: StickerGridsProps<R>['onChange'];
  closeAfterChange?: boolean;
  showEmojiTab?: boolean;
  onEmojiChange?: (emoji: any) => void;
}

const STICKER_GRIDS_GAP = 8;

const StickerTab = styled(StickerTabOrig)`
  background-color: var(--fett-bg-app);
  padding: 8px 2px;
  /* margin-bottom: 12px; */
  /* box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.2); */
`;
const defaultTrigger = ['click'];
const POPOVER_GRIDS_LAYOUT: StickerGridsProps<any>['layout'] = [6, 3];

const StickerPopover = <R extends Record<string, any>>(props: StickerPopoverProps<R>) => {
  const {
    stickers,
    layout = POPOVER_GRIDS_LAYOUT,
    size = 64,
    overlayClassName: overlayClassNameProp,
    trigger = defaultTrigger,
    onChange: onChangeProp,
    visible: visibleProp,
    showEmojiTab,
    onEmojiChange,
    ...popPorps
  } = props;
  const { getAntdPrefixedClassName } = useTheme();
  const [activePack, setActivePack] = useState('');
  const setConversationToggle = useSetConversationToggle('stickerHide');
  const [visible, setVisible] = useState<boolean | undefined>(undefined);
  const overlayClassName = useMemo(() => {
    const [perLine] = layoutParser(layout);
    return cx(
      css`
        .${getAntdPrefixedClassName('popover-inner-content')} {
          padding: 0;
          padding-top: ${STICKER_GRIDS_GAP}px;
          max-width: ${sizeParser(size) * perLine + STICKER_GRIDS_GAP * (perLine - 1) + 12}px;
        }
      `,
      overlayClassNameProp,
    );
  }, [getAntdPrefixedClassName, size, layout, overlayClassNameProp]);
  const { tabs: packs, titles: packTitles } = useMemo(() => {
    type Pack = StickerTabProps['tabs'][number];
    const packsMap: Record<Pack['key'], Pack> = {};
    const tabs: Pack[] = [];
    let hasPack = false;
    const titles: React.ReactNode[] = [];
    stickers.forEach((sticker) => {
      if (sticker.pack) {
        hasPack = true;
      }
      const { pack = 'OTHRES', packTitle, packLabel, imageUrl } = sticker;

      if (!packsMap[pack]) {
        const tab = {
          key: pack,
          // title: packTitle,
          label: packLabel || pack,
          thumb: packLabel ? '' : imageUrl,
        };
        packsMap[pack] = tab;
        titles[tabs.length] = packTitle;
        tabs.push(tab);
      }
    });
    return { tabs: hasPack ? tabs : [], titles, packsMap };
  }, [stickers]);
  const onChange = useMemoizedFn((sticker: Sticker<R>) => {
    if (isUndefined(visibleProp)) {
      setVisible(false);
    }
    onChangeProp?.(sticker);
  });
  const finalVisible = visibleProp ?? visible;
  const { curPack, showstickers, title } = useMemo(() => {
    const packDataIdx = (activePack && packs.findIndex((t) => t.key === activePack)) || 0;
    const packData = packs[packDataIdx];
    const title = packTitles[packDataIdx];
    const curPack = packData?.key || '';
    const showstickers = curPack ? stickers.filter((it) => it.pack === curPack) : stickers;
    return { packData, curPack, title, showstickers };
  }, [activePack, packTitles, packs, stickers]);
  const grids = useMemo(() => {
    return (
      <StickerGrids<R>
        key={curPack || 'default'}
        gap={STICKER_GRIDS_GAP}
        size={size}
        onChange={onChange}
        layout={layout}
        stickers={showstickers}
        showEmoji={activePack === 'emoji'}
        onEmojiChange={onEmojiChange}
      />
    );
    // }
  }, [curPack, layout, onChange, showstickers, size, activePack, onEmojiChange]);
  useEffect(() => {
    if (finalVisible && (!activePack || !packs.some((_) => _.key === activePack))) {
      const key = packs?.[0]?.key;
      if (key) {
        setActivePack(key);
      } else if (showEmojiTab) {
        setActivePack('emoji');
      }
    }
  }, [finalVisible, packs, showEmojiTab, activePack]);

  const tabs = useMemo(() => {
    return (
      <>
        <>{title ?? null}</>
        <StickerTab
          showEmoji={showEmojiTab}
          activeTabKey={curPack}
          onChange={setActivePack}
          tabs={packs}
        />
      </>
    );
  }, [title, packs, curPack, showEmojiTab]);

  useEffect(() => {
    setConversationToggle(finalVisible ? false : true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalVisible]);

  return (
    <Popover
      visible={finalVisible}
      overlayClassName={overlayClassName}
      trigger={trigger}
      content={
        <>
          {grids}
          {tabs}
        </>
      }
      {...popPorps}
      onVisibleChange={useMemoizedFn((v) => {
        if (isUndefined(visibleProp)) {
          setVisible(v);
        }
        if (popPorps.onVisibleChange) {
          popPorps.onVisibleChange(v);
        }
      })}
    />
  );
};
export default StickerPopover;
